import React, { useEffect, useMemo, useRef } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui'
import { cn } from '@/utils/common'

import { useTflViewerStore } from '../store'
import { Reviewer, ReviewerStatus } from '../types'

const dropdownKeyframes = `
  @keyframes dropdown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`

export interface ReviewerDetailsDialogProps {
  open: boolean
  onClose: () => void
  documentId: string
  anchorEl?: HTMLElement | null
}

const getReviewerInitials = (reviewer: Reviewer): string => {
  return `${reviewer.first_name?.[0] || ''}${reviewer.last_name?.[0] || ''}`.toUpperCase()
}

const ReviewerDialogItem = ({ reviewer }: { reviewer: Reviewer }) => {
  const initials = getReviewerInitials(reviewer)
  let reviewStatus: string = 'Pending'

  if (reviewer.status === ReviewerStatus.APPROVED) {
    reviewStatus = 'Approved'
  } else if (reviewer.status === ReviewerStatus.REJECTED) {
    reviewStatus = 'Query Raised'
  }

  return (
    <div className="flex items-center justify-between p-2 rounded-md transition-colors">
      <div className="flex items-center gap-2">
        <Avatar className="h-10 w-10 border border-black/10">
          <AvatarImage src={reviewer.avatar_url} alt={reviewer.first_name} />
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div>
          <div className="font-medium text-secondary-900 text-sm">
            {reviewer.first_name} {reviewer.last_name}
          </div>
          <div
            className={cn(
              'text-xs',
              reviewer.status === ReviewerStatus.APPROVED
                ? 'text-success-600'
                : 'text-secondary-500'
            )}
          >
            {reviewStatus}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ReviewerDetailsDialog: React.FC<ReviewerDetailsDialogProps> = ({
  open,
  onClose,
  documentId,
  anchorEl,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const { metadataByDocId } = useTflViewerStore()
  const metadata = metadataByDocId[documentId]
  const [position, setPosition] = React.useState({ top: 0, left: 0 })

  const reviewers = useMemo(() => {
    return metadata?.reviewers || []
  }, [metadata])

  useEffect(() => {
    if (open && anchorEl) {
      const rect = anchorEl.getBoundingClientRect()
      if (rect.height > 0) {
        setPosition({
          top: rect.bottom + 8,
          left: rect.left + rect.width / 2,
        })
      }
    }
  }, [open, anchorEl])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open) {
      dialog.show()
    } else {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dialog = dialogRef.current
      if (!dialog) return

      const rect = dialog.getBoundingClientRect()
      const isInDialog =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width

      if (!isInDialog && anchorEl && !anchorEl.contains(event.target as Node)) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose, anchorEl])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleCancel = (event: Event) => {
      event.preventDefault()
      onClose()
    }

    dialog.addEventListener('cancel', handleCancel)
    return () => {
      dialog.removeEventListener('cancel', handleCancel)
    }
  }, [onClose])

  if (!open) return null

  return (
    <>
      <style>{dropdownKeyframes}</style>
      <dialog
        ref={dialogRef}
        className={cn(
          'fixed w-60 rounded-lg border border-gray-200 bg-white shadow-lg',
          'p-0 m-0 -translate-x-1/2'
        )}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 9999,
          animation: 'dropdown 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          transformOrigin: 'top center',
        }}
        aria-labelledby="reviewer-details-title"
      >
        <div className="max-h-80 overflow-y-auto p-sm">
          {reviewers.length === 0 ? (
            <div className="text-center py-6 text-secondary-500 text-sm">
              No reviewers assigned to this document
            </div>
          ) : (
            <div className="p-x-1">
              {reviewers.map((reviewer: Reviewer) => (
                <ReviewerDialogItem key={reviewer.id} reviewer={reviewer} />
              ))}
            </div>
          )}
        </div>
      </dialog>
    </>
  )
}
